import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, Dispatch, ReactElement, ReactNode,
              SetStateAction } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Angebot, AngebotVersion, Ansprechpartner, BelegAblage, Benutzer,
              PositionsStand, Unternehmen, Vorlagenauswahl } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { Belegvorschau } from "./Belegvorschau.js";
import { PositionsEditor, PlatzhalterTextfeld,
         laufzeitVorschauWerte } from "./PositionsEditor.js";
import type { PositionsEditorClient } from "./PositionsEditor.js";
import { Aufklapper, Kopfblock, useKopffaltung } from "./Kopffaltung.js";
import type { Kopffaltung } from "./Kopffaltung.js";
import { BelegParteien, PARTEIEN_KOPFFELDER } from "./BelegParteien.js";
import type { BelegParteienModalClient } from "./BelegParteien.js";
import { Zahlplan, ZAHLPLAN_KOPFFELDER, istZahlplanFeld } from "./Zahlplan.js";
import type { ZahlplanModalClient } from "./Zahlplan.js";
import { Anhaenge, ANHAENGE_KOPFFELDER } from "./Anhaenge.js";
import type { AnhaengeClient } from "./Anhaenge.js";
import { formatBetrag, formatDatum } from "../format.js";
import { ANGEBOT_STATUS_LABEL } from "../vorgangsstatus.js";
import { apiMeldung, betragText, betragZahl, entwuerfeGleich,
         kopfBetragText, kopffeldAusFehler, kopffeldId, LAUFZEIT_KOPFFELDER,
         nettobetragLabel, oderNull,
         pdfMeldungsStil, speichereBlob, speichereParteien, speichereZahlplan,
         useParteienGriff, useZahlplanGriff } from "./vorgangsdetail.js";
import type { KopffeldTabelle, PdfMeldung } from "./vorgangsdetail.js";
// Die Klassennamen bleiben `kang__` (Etappe 1, Aufgabe 2: keine Umbenennung an
// herausgelösten Bestandsteilen) — umgezogen ist in Fixrunde 1 nur die DATEI: Die Regeln
// dessen, was DIESER Baustein zeichnet, stehen jetzt hier statt im Stilblatt des Reiters.
// Damit bringt der Baustein seine Gestalt überallhin mit, ohne dass components/ nach views/
// hineingreifen müsste.
import "./AngebotDetail.css";

/**
 * Das ANGEBOTSDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 2).
 *
 * Herausgelöst aus views/KundeAngebote.tsx, damit der Akten-Reiter und der spätere
 * Angebots-BEREICH (Aufgabe 3) buchstäblich DASSELBE Detail zeichnen — nicht zwei Masken,
 * die sich anfangs gleichen und mit dem dritten Fix auseinanderlaufen. Der Umbau ist rein
 * strukturell: Es kommt kein Verhalten hinzu und es fällt keines weg.
 *
 * Was der Baustein trägt: die Kopfzeile des Vorgangs (Nummer, Kürzel, Titel, Status), die
 * Datenzeile (Betrag, Gültigkeit, Ansprechpartner), das Bearbeiten samt Positions-Editor
 * und Vorlagen-Anwenden, die Statusaktionen entlang der erlaubten Übergänge, „Als PDF"
 * mitsamt der `fields[]`-Befundliste und die Angebot→Auftrag-Naht.
 *
 * Was er NICHT weiss: dass es eine Kundenakte gibt. Die Liste, das Anlegen, der
 * Meldungsstreifen und die Frage, WELCHER Vorgang gerade bearbeitet wird, gehören dem
 * Rahmen — er reicht sie als Eigenschaften und Rückrufe herein (`bearbeitung`, `gesperrt`,
 * `onBearbeiten`/`onSchliessen`/`onNeuLaden`/`onHinweis`/`onFortschreiben`). So trägt der
 * Bereich in Aufgabe 3 denselben Baustein, ohne dass hier ein Zweig „im Bereich" entsteht;
 * und ein späterer Rechnungs-Bereich könnte es genauso.
 *
 * Der Vorgang kommt als GELADENER Satz herein (`angebot`), nicht als blosse Kennung: Der
 * Reiter führt seine Liste bereits, und ein Nachladen je Zeile wäre bei zwanzig Angeboten
 * zwanzig zusätzliche Abrufe — eine Verhaltensänderung, die dieser Umbau ausdrücklich nicht
 * machen darf. Der Bereich sucht seinen Satz in Aufgabe 3 genauso aus seiner eigenen Liste.
 */

/** Nur die Klientenmethoden, die das DETAIL braucht — Liste und Anlegen bleiben beim
 *  Rahmen (siehe `AngeboteClient` in views/KundeAngebote.tsx). */
export type AngebotDetailClient = Pick<ApiClient,
  "getOffer" | "updateOffer" | "setOfferStatus"
  | "neueOfferVersion" | "kopiereOffer" | "listOfferVersions"
  | "listContacts" | "listUsers" | "listPlaceholders" | "listTemplates" | "applyTemplate"
  | "renderOffer"
  // Belegvorschau (24.09.2026): die Seitenbilder und die PDF-Ablage in der Kundenakte.
  | "vorschauOffer" | "getOfferAblage" | "createOfferAblage">
  & PositionsEditorClient
  & BelegParteienModalClient
  & ZahlplanModalClient
  // Der Kasten „Anhänge" (Paket „Textbausteine als Anhang", 24.09.2026) — fünf Wege, von
  // denen das Angebot vier benutzt (`anhangSichtbarkeit` gibt es nur am Auftrag; der
  // Ausschnitt bleibt trotzdem gemeinsam, damit es nur EINEN Kasten gibt).
  & AnhaengeClient;

/**
 * Die AUSWAHLLISTEN, die Anlege- und Bearbeitungsmaske gemeinsam brauchen. Sie werden vom
 * RAHMEN geladen (`useAngebotsUmfeld`) und hier hereingereicht, nicht je Detail geholt:
 * Sonst zöge jede Listenzeile ihre eigenen Ansprechpartner, Benutzer, Platzhalter und
 * Vorlagen — vier Abrufe mal zwanzig Zeilen, für Daten, die für alle dieselben sind.
 */
export type AngebotsUmfeld = {
  kontakte: Ansprechpartner[];
  benutzer: Benutzer[];
  platzhalter: string[];
  vorlagen: Vorlagenauswahl[];
};

/**
 * Lädt die Auswahllisten einer Angebots-Ansicht: aktive Ansprechpartner des Kunden, aktive
 * Benutzer, die Platzhalternamen und die Vorlagen des betroffenen Unternehmens.
 *
 * Scheitert einer der Abrufe, bleibt die Ansicht bedienbar — die Felder sind optional, und
 * „ohne Vorlage" bleibt immer möglich. `vorlagenFirma` ist das Unternehmen, dessen Vorlagen
 * gerade gebraucht werden (leer = keine): Eine Vorlage gehört immer genau einer Firma, eine
 * gemeinsame Liste über alle wäre eine Auswahl, aus der die Hälfte 422 ergäbe.
 */
export function useAngebotsUmfeld(
  client: Pick<AngebotDetailClient,
    "listContacts" | "listUsers" | "listPlaceholders" | "listTemplates">,
  kundeId: string,
  vorlagenFirma: string,
): AngebotsUmfeld {
  const [kontakte, setKontakte] = useState<Ansprechpartner[]>([]);
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlagenauswahl[]>([]);

  useEffect(() => {
    let ab = false;
    client.listContacts(kundeId)
      .then(({ items }) => { if (!ab) setKontakte(items.filter((k) => k.status === "ACTIVE")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  useEffect(() => {
    let ab = false;
    client.listUsers()
      .then(({ items }) => { if (!ab) setBenutzer(items.filter((b) => b.active)); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listPlaceholders()
      .then(({ variablen }) => { if (!ab) setPlatzhalter(variablen); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    if (vorlagenFirma === "") { setVorlagen([]); return; }
    let ab = false;
    client.listTemplates(vorlagenFirma)
      .then(({ items }) => { if (!ab) setVorlagen(items); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten; „ohne Vorlage" bleibt möglich */ });
    return () => { ab = true; };
  }, [client, vorlagenFirma]);

  return { kontakte, benutzer, platzhalter, vorlagen };
}

type OfferStatus = Angebot["status"];

/**
 * Die erlaubten Statusübergänge — bewusst hier NACHGEBILDET, nicht aus @kundeninfo/domain
 * importiert: web darf nur `ui` und `api-client` importieren (check-boundaries). Die Regel
 * ist die aus der Spec (offerUebergangErlaubt): `DRAFT → SENT`; `SENT → ACCEPTED | REJECTED`;
 * aus jedem Nicht-Endzustand `→ WITHDRAWN`. Endzustände (ACCEPTED/REJECTED/WITHDRAWN) bieten
 * nichts an. Der Server prüft denselben Übergang noch einmal (422), das hier ist die
 * Bedienführung, nicht die Wahrheit.
 */
function erlaubteFolgestatus(von: OfferStatus): OfferStatus[] {
  if (von === "DRAFT") return ["SENT", "WITHDRAWN"];
  if (von === "SENT") return ["ACCEPTED", "REJECTED", "WITHDRAWN"];
  // ACCEPTED, REJECTED, WITHDRAWN und SUPERSEDED sind Endzustände. SUPERSEDED wird
  // ausserdem NIE von Hand gesetzt — er entsteht allein durch „Neue Version".
  return [];
}

/** Der Verb-Knopftext je Zielstatus (Bedienführung: was der Klick TUT). */
const STATUS_AKTION: Record<OfferStatus, string> = {
  DRAFT: "Zurück in Entwurf",
  // NICHT „Senden": Der Knopf verschickt nichts, er setzt einen Status. Der alte Text
  // war ein Versprechen, das die Anwendung nicht einlöst (Spec, Abschnitt
  // „Oberfläche"); der Hinweis daneben sagt es noch einmal ausdrücklich.
  SENT: "Als gesendet markieren",
  ACCEPTED: "Annehmen",
  REJECTED: "Ablehnen",
  WITHDRAWN: "Zurückziehen",
  // Steht nur der Vollständigkeit halber in der Tabelle — SUPERSEDED taucht in
  // `erlaubteFolgestatus` nie auf und bekommt deshalb nie einen Knopf.
  SUPERSEDED: "Veraltet",
};

/** Der Satz neben „Als gesendet markieren" (Spec, wörtlich). */
const SENT_HINWEIS = "Setzt nur den Status. Es wird nichts versendet.";

/**
 * Der Versionszusatz — NACHGEBILDET wie `erlaubteFolgestatus`, weil `web` nur `ui` und
 * `api-client` einführen darf (check-boundaries). Die Elle ist `versionsZusatz` in
 * `@kundeninfo/domain`; dieselbe Regel, dieselben Worte: leer bei 1, sonst „v<n>".
 */
function versionsZusatz(versionNumber: number): string {
  return Number.isInteger(versionNumber) && versionNumber >= 2 ? `v${versionNumber}` : "";
}

/** Die deutschen Beschriftungen der Rückfragen. Sie stehen hier und nicht im Aufruf,
 *  damit der Wortlisten-Wächter sie findet und beide Sätze nebeneinander lesbar sind. */
const FRAGE_NEUE_VERSION =
  "Es entsteht eine neue Version dieses Angebots mit derselben Nummer. Die aktuelle "
  + "Fassung wird dabei zur alten Version und lässt sich danach nicht mehr ändern. "
  + "Fortfahren?";
const FRAGE_WIEDERHERSTELLEN =
  "Es entsteht eine neue Version mit den Inhalten DIESER älteren Fassung. Die aktuelle "
  + "Fassung wird dabei zur alten Version und lässt sich danach nicht mehr ändern. "
  + "Fortfahren?";
const FRAGE_KOPIEREN =
  "Es entsteht ein neues Angebot mit EIGENER Nummer und denselben Inhalten. Das "
  + "bisherige Angebot bleibt unverändert. Fortfahren?";

/** Klasse und Rolle der PDF-Rückmeldung im Block dieser Ansicht (siehe vorgangsdetail.ts).
 *  EIGENER Blockname statt eines über Angebot, Auftrag und Rechnung geteilten: Dieselbe
 *  Klasse über drei Ansichten hiesse, dass eine Stilanpassung an der einen die beiden
 *  anderen still mitverändert. Die Gestalt ist bewusst dieselbe, der Name nicht. */
const PDF_MELDUNGS_STIL = pdfMeldungsStil("kang");

/** Die Formularwerte. Alle Angaben sind Zeichenketten, damit ein geleertes Feld erst beim
 *  Absenden zu null/0 wird. */
export type AngebotEntwurf = {
  companyId: string;
  /** Die beim ANLEGEN gewählte Vorlage („" = ohne Vorlage). Sie geht als `vorlageId` mit —
   *  Anlegen und Anwenden in EINEM Zug; am bestehenden Angebot hat das Anwenden einen
   *  eigenen Weg (siehe `vorlageAnwenden`). */
  vorlageId: string;
  title: string;
  netAmount: string;
  contactId: string;
  responsibleUserId: string;
  validUntil: string;
  note: string;
  /** Rohtexte MIT ihren `#{…}`-Tokens — gespeichert wird genau das, aufgelöst wird erst
   *  fürs PDF (die Vorschau darunter ist nur eine Lesehilfe). */
  introText: string;
  outroText: string;
  /**
   * Die Laufzeitfelder aus Migration 031. Die WERTE als Zeichenkette, damit ein geleertes
   * Feld beim Absenden zu `null` wird statt zu 0 — „keine Mindestlaufzeit" und „null
   * Monate" sind zwei verschiedene Aussagen.
   *
   * Die EINHEITEN behalten ihren Wert auch bei leerem Feld: Sonst würde eine einmal
   * gewählte Angabe „Jahre" durch ein Leeren des Zahlenfeldes still zu „Monate"
   * (dieselbe Überlegung wie bei `noticeReference` seit Migration 008). Vorher gab es hier
   * nur zwei nackte Monatszahlen (`minimumTermMonths`/`renewalPeriodMonths`) und keine
   * Vertragslaufzeit — Migration 031 macht daraus vier Paare aus Zahl und Einheit.
   */
  termValue: string;
  termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string;
  minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string;
  renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string;
  noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
  /** Die Periodenregel des Zahlplans (Migration 026) — Kalender- oder Vertragsjahr. Sie
   *  steht am KOPF, weil sie für den ganzen Beleg gilt, nicht je Rate. */
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

export function leererAngebotEntwurf(
  companies: Unternehmen[], arbeitsunternehmenId: string | null,
): AngebotEntwurf {
  return {
    // Das Arbeitsunternehmen (ME-Etappe 1, Aufgabe 7), sofern es wählbar ist. Sonst wie
    // bisher: Bei genau einem Unternehmen (der häufige Fall: eine Kundenbeziehung) ist es
    // vorgewählt; bei mehreren muss bewusst gewählt werden.
    companyId: companies.find((c) => c.id === arbeitsunternehmenId)?.id
      ?? (companies.length === 1 ? companies[0]!.id : ""),
    vorlageId: "",
    title: "", netAmount: "", contactId: "", responsibleUserId: "", validUntil: "", note: "",
    introText: "", outroText: "",
    termValue: "", termUnit: "MONTHS",
    minimumTermValue: "", minimumTermUnit: "MONTHS",
    renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
    noticePeriodValue: "", noticePeriodUnit: "MONTHS", noticeReference: "TERM_END",
    periodBasis: "CALENDAR_YEAR",
  };
}

export function angebotEntwurfAus(a: Angebot): AngebotEntwurf {
  return {
    companyId: a.companyId,
    // Beim Bearbeiten trägt das Formular KEINE Vorlage: Ein bestehendes Angebot wird nicht
    // „aus einer Vorlage angelegt" — dafür gibt es den Anwenden-Weg, der anhängt.
    vorlageId: "",
    title: a.title,
    // Deutsch geschrieben, weil deutsch gelesen wird (siehe betragText) — `String()` ergäbe
    // „200.55", und das läse `betragZahl` als 20055.
    netAmount: betragText(a.netAmount),
    contactId: a.contactId ?? "",
    responsibleUserId: a.responsibleUserId ?? "",
    validUntil: a.validUntil ?? "",
    note: a.note ?? "",
    introText: a.introText ?? "",
    outroText: a.outroText ?? "",
    termValue: a.termValue === null ? "" : String(a.termValue),
    termUnit: a.termUnit ?? "MONTHS",
    minimumTermValue: a.minimumTermValue === null ? "" : String(a.minimumTermValue),
    minimumTermUnit: a.minimumTermUnit ?? "MONTHS",
    renewalPeriodValue: a.renewalPeriodValue === null ? "" : String(a.renewalPeriodValue),
    renewalPeriodUnit: a.renewalPeriodUnit ?? "MONTHS",
    noticePeriodValue: a.noticePeriodValue === null ? "" : String(a.noticePeriodValue),
    noticePeriodUnit: a.noticePeriodUnit ?? "MONTHS",
    noticeReference: a.noticeReference ?? "TERM_END",
    periodBasis: a.periodBasis,
  };
}

/**
 * Die Kopffelder der Angebotsmaske: SERVER-Feldname → Endung der Feld-Id im Formular.
 *
 * Sie steht hier, unmittelbar neben der Prüfung, die dieselbe Tabelle benutzt — die
 * Begründung im Einzelnen bei `KopffeldTabelle` (vorgangsdetail.ts). Kurz: Es gibt zwei
 * Quellen von Feldbefunden (diese Maske und der Server), beide müssen den eingefalteten
 * Kopfblock aufklappen, und beide gehen deshalb durch diese eine Tabelle.
 *
 * Notiz, Einleitungs- und Ausleitungstext stehen bewusst NICHT darin: Sie liegen ausserhalb
 * des Kopfblocks und sind immer zu sehen.
 */
export const ANGEBOT_KOPFFELDER: KopffeldTabelle = {
  companyId: "company",
  title: "title",
  netAmount: "amount",
  contactId: "contact",
  responsibleUserId: "user",
  validUntil: "valid",
  periodBasis: "periodbasis",
  ...LAUFZEIT_KOPFFELDER,
  // Die Felder des Kopfblocks PARTEIEN (Adressen Etappe 3) — sie stehen IM Kopfblock und
  // werden in einem zweiten Weg gespeichert (`setBelegParteien`). Ein Befund von dort
  // muss denselben Weg ins Bild finden wie einer am Titel, sonst stünde die Meldung über
  // einem eingeklappten Feld.
  ...PARTEIEN_KOPFFELDER,
  // Der Abschnitt ZAHLPLAN (Migration 026) — dieselbe Überlegung wie an PARTEIEN_KOPFFELDER:
  // ein dritter Speicherweg, dessen Befunde denselben Weg ins Bild finden müssen. Die
  // SERVER-Feldnamen tragen einen Ratenindex (`raten[2].sharePercent`) und passen in keine
  // feste Tabelle — deshalb behandelt `zeigeAngebotsFehlerFeld` sie VOR dieser Tabelle
  // gesondert (`istZahlplanFeld`). Der Eintrag hier bleibt trotzdem stehen: Er hält
  // `AUFTRAG_KOPFFELDER`/`ANGEBOT_KOPFFELDER` beisammen mit `ZAHLPLAN_KOPFFELDER` und trägt
  // die einzige Kennung, die `kopffeldId` für „raten" (ohne Index) kennt.
  ...ZAHLPLAN_KOPFFELDER,
  // Der Kasten „Anhänge" — heute LEER (er hat kein Kopffeld, die Anlagen stehen unter
  // dem Zahlplan). Der Spread steht trotzdem hier, damit ein späteres Kopffeld des
  // Kastens nicht an zwei Stellen nachgetragen werden muss (Muster `PARTEIEN_KOPFFELDER`).
  ...ANHAENGE_KOPFFELDER,
};

/**
 * Der Befund der Feldprüfung: der Satz für den Bearbeiter UND das Feld, um das es geht —
 * bei seinem SERVERNAMEN, damit Masken- und Serverweg durch dieselbe Tabelle laufen
 * (`ANGEBOT_KOPFFELDER`).
 *
 * Das Feld kam mit dem eingefalteten Kopfblock dazu (Paket „Positionen mit Präsenz",
 * 08.09.2026): Alle drei Pflichtangaben stehen darin, und eingeklappt sähe niemand den
 * Satz an einem Feld, das gar nicht zu sehen ist.
 */
export type AngebotBefund = {
  meldung: string;
  feld: "companyId" | "title" | "netAmount";
};

/** Die gemeinsame Prüfung für Anlegen und Bearbeiten (null = in Ordnung). */
export function pruefeAngebot(
  entwurf: AngebotEntwurf, brauchtCompany: boolean,
): AngebotBefund | null {
  if (brauchtCompany && entwurf.companyId === "") {
    return { meldung: "Bitte ein Unternehmen wählen.", feld: "companyId" };
  }
  if (entwurf.title.trim() === "") {
    return { meldung: "Bitte einen Titel angeben.", feld: "title" };
  }
  if (Number.isNaN(betragZahl(entwurf.netAmount))) {
    return { meldung: "Bitte einen gültigen Betrag angeben.", feld: "netAmount" };
  }
  return null;
}

/**
 * Den Kopfblock auf das Feld öffnen, das ein Befund nennt — der eine Handgriff für BEIDE
 * Quellen (Maskenprüfung und Server-422).
 *
 * `feld` ist entweder der Servername aus `pruefeAngebot` oder der Fehler selbst; steht das
 * Feld nicht im Kopfblock (Notiz, Texte, Vorlage) oder trägt der Fehler gar keine Befunde
 * (Netzabbruch), geschieht nichts — die Meldung am Fuß steht dann über einem Feld, das
 * ohnehin zu sehen ist.
 */
export function zeigeAngebotsFeld(
  kopf: Kopffaltung, praefix: string, feld: string,
): void {
  const ziel = kopffeldId(ANGEBOT_KOPFFELDER, praefix, feld);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

export function zeigeAngebotsFehlerFeld(
  kopf: Kopffaltung, praefix: string, fehler: unknown,
): void {
  // Ein Befund am ZAHLPLAN trägt einen Ratenindex (`raten[2].sharePercent`) und passt in
  // keine feste Tabelle — `istZahlplanFeld` erkennt ihn VOR dem Tabellen-Nachschlag, sonst
  // fände `kopffeldAusFehler` nie eine Übereinstimmung und der Abschnitt bliebe zu.
  if (fehler instanceof ApiError
      && (fehler.fields ?? []).some((f) => istZahlplanFeld(f.field))) {
    kopf.zeigeFeld(`${praefix}-zahlplan`);
    return;
  }
  const ziel = kopffeldAusFehler(ANGEBOT_KOPFFELDER, praefix, fehler);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

export type AngebotKopffelder = {
  title: string; netAmount: number; contactId: string | null;
  responsibleUserId: string | null; validUntil: string | null; note: string | null;
  introText: string | null; outroText: string | null;
  termValue: number | null; termUnit: "MONTHS" | "YEARS" | null;
  minimumTermValue: number | null; minimumTermUnit: "MONTHS" | "YEARS" | null;
  renewalPeriodValue: number | null; renewalPeriodUnit: "MONTHS" | "YEARS" | null;
  noticePeriodValue: number | null;
  noticePeriodUnit: "MONTHS" | "YEARS" | null;
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END" | null;
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

/** Die Kopffelder, die Anlegen und Ändern teilen (companyId gehört nur zum Anlegen — der
 *  Server lässt sie beim Ändern nicht zu). */
export function angebotKopffelder(entwurf: AngebotEntwurf): AngebotKopffelder {
  /** Ein Wert/Einheit-PAAR für den Server: leer heisst BEIDES null. Ausgeschrieben und
   *  nicht gestreut, weil `exactOptionalPropertyTypes` ein `undefined` anders behandelt
   *  als einen fehlenden Schlüssel. */
  function paar(wert: string, einheit: "MONTHS" | "YEARS") {
    const kern = wert.trim();
    return kern === ""
      ? { wert: null, einheit: null }
      : { wert: Number(kern), einheit };
  }
  const term = paar(entwurf.termValue, entwurf.termUnit);
  const mindest = paar(entwurf.minimumTermValue, entwurf.minimumTermUnit);
  const verlaengerung = paar(entwurf.renewalPeriodValue, entwurf.renewalPeriodUnit);
  const frist = paar(entwurf.noticePeriodValue, entwurf.noticePeriodUnit);
  return {
    title: entwurf.title.trim(),
    // Mit Positionen ist der Betrag berechnet; das Feld zeigt ihn nur an und schickt ihn
    // unverändert zurück (der Server nimmt genau diesen Wert an, siehe gepruefterBetrag).
    netAmount: betragZahl(entwurf.netAmount),
    contactId: oderNull(entwurf.contactId),
    responsibleUserId: oderNull(entwurf.responsibleUserId),
    validUntil: oderNull(entwurf.validUntil),
    note: oderNull(entwurf.note),
    introText: oderNull(entwurf.introText),
    outroText: oderNull(entwurf.outroText),
    termValue: term.wert, termUnit: term.einheit,
    minimumTermValue: mindest.wert, minimumTermUnit: mindest.einheit,
    renewalPeriodValue: verlaengerung.wert, renewalPeriodUnit: verlaengerung.einheit,
    noticePeriodValue: frist.wert, noticePeriodUnit: frist.einheit,
    // Der BEZUGSPUNKT hängt weiter an der Frist: Ohne Zahl gibt es keinen Stichtag.
    noticeReference: frist.wert === null ? null : entwurf.noticeReference,
    periodBasis: entwurf.periodBasis,
  };
}

/** Die Werte für die Platzhalter-Vorschau — genau das, was die Ansicht ohnehin geladen hat.
 *  Was fehlt (z. B. eine Anschrift), erscheint gar nicht erst und bleibt damit als
 *  sichtbares Token stehen. `angebot` ist beim ANLEGEN null — eine Angebotsnummer gibt es
 *  dann noch nicht. */
export function angebotVorschauWerte(arg: {
  entwurf: AngebotEntwurf;
  companies: Unternehmen[];
  kontakte: Ansprechpartner[];
  angebot: Angebot | null;
  kundeName?: string | undefined;
}): Record<string, string> {
  const { entwurf, companies, kontakte, angebot, kundeName } = arg;
  const werte: Record<string, string> = {
    ...laufzeitVorschauWerte({
      termValue: entwurf.termValue, termUnit: entwurf.termUnit,
      minimumTermValue: entwurf.minimumTermValue, minimumTermUnit: entwurf.minimumTermUnit,
      renewalPeriodValue: entwurf.renewalPeriodValue,
      renewalPeriodUnit: entwurf.renewalPeriodUnit,
      noticePeriodValue: entwurf.noticePeriodValue,
      noticePeriodUnit: entwurf.noticePeriodUnit,
      noticeReference: entwurf.noticeReference,
    }),
    "datum.heute": formatDatum(new Date().toISOString()),
  };
  if (kundeName !== undefined && kundeName !== "") werte["kunde.name"] = kundeName;
  const firma = companies.find((c) => c.id === entwurf.companyId);
  if (firma !== undefined) werte["firma.name"] = firma.name;
  const kontakt = kontakte.find((k) => k.id === entwurf.contactId);
  if (kontakt !== undefined) {
    werte["ansprechpartner.name"] = kontakt.anzeigename;
    // Migration 025: die Briefanrede steht NEBEN dem Namen (masterdataRules.ts) — sie ist
    // eine Ableitung aus Geschlecht, Titel und Namen, kein eigenes Eingabefeld.
    werte["ansprechpartner.anrede"] = kontakt.briefanrede;
  }
  if (angebot !== null) werte["angebot.nummer"] = angebot.offerNumber;
  if (entwurf.validUntil !== "") {
    werte["angebot.gueltigBis"] = formatDatum(entwurf.validUntil);
  }
  return werte;
}

export type AngebotFelderArgs = {
  praefix: string;
  mitUnternehmen: boolean;
  companies: Unternehmen[];
  umfeld: AngebotsUmfeld;
  entwurf: AngebotEntwurf;
  setEntwurf: Dispatch<SetStateAction<AngebotEntwurf>>;
  /** Hat der Vorgang Positionen? Dann ist der Nettobetrag berechnet und nur noch Anzeige. */
  hatPositionen: boolean;
  feldFehler: string | null;
  vorschauWerte: Record<string, string>;
  /** Der Griff auf die Einfaltung des Kopfblocks — er gehört der MASKE (siehe
   *  components/Kopffaltung.tsx), weil nur sie den Startzustand und die Rücksetzpunkte
   *  kennt. */
  kopf: Kopffaltung;
  /**
   * Der POSITIONSBLOCK, der zwischen Einleitungs- und Ausleitungstext steht (Entscheid Dirk
   * 08.09.2026: dort wird die Arbeit geleistet, also bekommt er die Mitte des Formulars).
   *
   * Als Schlitz und nicht hier gezeichnet: Der Editor hängt am geladenen Vorgang samt
   * seiner Version, und beim ANLEGEN gibt es beides noch nicht — dort bleibt der Schlitz
   * leer, und die Reihenfolge der übrigen Blöcke ist trotzdem dieselbe. Die Alternative
   * wäre ein zweiter Aufbau der Maske je Weg gewesen, also genau die Kopie, die sich beim
   * dritten Fix auseinanderlebt.
   */
  positionen?: ReactNode | undefined;
  /**
   * Der Kopfblock PARTEIEN (Käufer, Anschriften, Bestellnummer) — als Schlitz und nicht
   * hier gezeichnet, aus demselben Grund wie `positionen`: Er hängt am geladenen Vorgang,
   * und beim ANLEGEN gibt es den noch nicht. Dort bleibt der Schlitz leer; die
   * Reihenfolge der übrigen Blöcke ist trotzdem dieselbe.
   */
  parteien?: ReactNode | undefined;
  /**
   * Der Abschnitt ZAHLUNGSVEREINBARUNG — als Schlitz und aus demselben Grund wie
   * `parteien`: Er hängt am geladenen Vorgang, und beim ANLEGEN gibt es den noch nicht.
   */
  zahlplan?: ReactNode | undefined;
  /**
   * Der Kasten „Anhänge" — als Schlitz aus demselben Grund wie `zahlplan`: Er hängt am
   * geladenen Vorgang, und beim ANLEGEN gibt es den noch nicht.
   *
   * Anders als `parteien` und `zahlplan` steht er NICHT im Kopfblock, sondern direkt
   * dahinter, immer sichtbar: Wer bearbeitet, will die Anlagen ohne einen weiteren Klick
   * sehen (der Kopfblock beginnt am bestehenden Vorgang eingeklappt, siehe
   * `useKopffaltung(false)` — ein Kasten DARIN wäre bis zum Aufklappen unsichtbar, und
   * genau das ist am Ausleitungstext und an den Positionen bereits die Ausnahme, keine
   * Regel). Fachlich gehört er trotzdem zu den KONDITIONEN und steht deshalb unmittelbar
   * unter dem Zahlplan, dem letzten Feld im Kopfblock (Spec: „Kasten Anhänge unter dem
   * Zahlplan").
   */
  anhaenge?: ReactNode | undefined;
};

/**
 * Die Felder von Anlege- und Bearbeitungsmaske. Bewusst eine Funktion, die JSX ZURÜCKGIBT,
 * und keine eigene Komponente: eine im Render definierte Komponente bekäme bei jedem
 * Neuzeichnen eine neue Identität — React hängte ihren Teilbaum ab und neu auf, und der
 * Fokus spränge nach jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner).
 * Sie ist auch nach dem Herauslösen des Bausteins gemeinsam: Der Rahmen zeichnet mit ihr
 * seine ANLAGE, das Detail sein Bearbeiten.
 *
 * **Die Reihenfolge ist der Inhalt dieses Pakets** (Entscheid Dirk 08.09.2026, „Positionen
 * mit Präsenz"): eingefalteter Kopfblock, dann Notiz und Einleitungstext, dann die
 * POSITIONEN, dann der Ausleitungstext. Sie steht genau EINMAL — hier — und gilt damit für
 * Akte wie Bereich, Anlegen wie Bearbeiten.
 */
export function angebotFormularFelder(a: AngebotFelderArgs): ReactElement {
  const { praefix, companies, umfeld, entwurf, setEntwurf, hatPositionen } = a;
  function setze(feld: keyof AngebotEntwurf) {
    return (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setEntwurf((v) => ({ ...v, [feld]: e.target.value }));
  }
  return (
    <div className="kang__maske">
      <Kopfblock faltung={a.kopf} titel={entwurf.title}
        betragLabel={nettobetragLabel(hatPositionen)}
        betrag={kopfBetragText(entwurf.netAmount)}
        inhaltId={`${praefix}-kopfdaten`}>
        {angebotKopffeldRaster(a, setze)}
        {a.parteien}
        {a.zahlplan}
      </Kopfblock>

      {/* Der Kasten „Anhänge" — direkt hinter dem Kopfblock und OHNE eigenen Aufklapper
          (siehe die Begründung an `anhaenge` in `AngebotFelderArgs`): Wer bearbeitet, soll
          die Anlagen sehen, ohne zuerst den eingeklappten Kopfblock zu öffnen. */}
      {a.anhaenge}

      {/* Notiz und Einleitungstext gleich unter dem Kopfblock: Sie sind der Text, der den
          Vorgang eröffnet, und sie werden beim Bearbeiten häufig angefasst — anders als
          Laufzeit und Kündigungsfrist, die einmal stehen und dann stehen bleiben. */}
      <div className="kang__texte">
        <div className="kang__feld">
          <label htmlFor={`${praefix}-note`} className="kang__label">Notiz</label>
          <textarea id={`${praefix}-note`} className="kang__textarea" rows={3}
            value={entwurf.note} onChange={setze("note")} />
        </div>

        {/* Einleitungstext: Rohtext mit Tokens, darunter die leichte Vorschau. Beides kommt
            aus der wiederverwendeten Editor-Komponente — in dieser Ansicht nachgebaut, gäbe
            es die Vorschau zweimal. */}
        <PlatzhalterTextfeld id={`${praefix}-intro`} label="Einleitungstext"
          wert={entwurf.introText}
          onChange={(wert) => setEntwurf((v) => ({ ...v, introText: wert }))}
          variablen={umfeld.platzhalter} werte={a.vorschauWerte} />
      </div>

      {a.positionen}

      {/* Der Ausleitungstext schliesst den Beleg ab — also steht er hinter dem, was er
          abschliesst. Seine Hinweiszeilen bleiben an IHM: Seit die Positionen dazwischen
          stehen, wäre eine gemeinsame Fussnote für beide Textfelder eine Fussnote, die zu
          einem Feld gehört, das man von ihr aus nicht mehr sieht. */}
      <div className="kang__texte">
        <PlatzhalterTextfeld id={`${praefix}-outro`} label="Ausleitungstext"
          wert={entwurf.outroText}
          onChange={(wert) => setEntwurf((v) => ({ ...v, outroText: wert }))}
          variablen={umfeld.platzhalter} werte={a.vorschauWerte} />
      </div>

      {/* Der Feldbefund steht am Ende der Maske, dicht über der Fußleiste: Dort wurde
          „Speichern" gedrückt, und dort wird er gesucht. Betrifft er ein Feld im
          eingeklappten Kopfblock, hat die Maske ihn bereits aufgeklappt und den Fokus
          hineingesetzt (siehe Kopffaltung.zeigeFeld). */}
      {a.feldFehler !== null && (
        <p className="kang__feldfehler" role="alert">{a.feldFehler}</p>
      )}
    </div>
  );
}

/** Das zweispaltige Raster der KOPFFELDER — der Inhalt des einfaltbaren Blocks. Eigene
 *  Funktion allein der Lesbarkeit halber: Sie hat keinen eigenen Zustand und keine eigene
 *  Identität (kein Komponententyp), zeichnet also weiterhin im selben Zug wie die Maske. */
function angebotKopffeldRaster(
  a: AngebotFelderArgs,
  setze: (feld: keyof AngebotEntwurf) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
): ReactElement {
  const { praefix, companies, umfeld, entwurf, setEntwurf, hatPositionen } = a;
  return (
    <div className="kang__felder">
      {a.mitUnternehmen && (
        <div className="kang__feld">
          <label htmlFor={`${praefix}-company`} className="kang__label">Unternehmen</label>
          <select id={`${praefix}-company`} className="kang__select"
            value={entwurf.companyId} onChange={setze("companyId")}>
            {companies.length !== 1 && <option value="">Bitte wählen …</option>}
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="kang__feld">
        <label htmlFor={`${praefix}-title`} className="kang__label">Titel</label>
        <input id={`${praefix}-title`} className="kang__eingabe" type="text"
          value={entwurf.title} onChange={setze("title")} />
      </div>

      {/* Sobald der Vorgang Positionen hat, ist der Betrag BERECHNET: Das Feld zeigt ihn
          nur noch an. Von Hand gesetzt wäre er eine zweite Wahrheit — der Server weist ihn
          als Feldfehler ab (BETRAG_BERECHNET), und die Maske soll das gar nicht erst
          anbieten. `readOnly` statt `disabled`: Der Wert bleibt lesbar, kopierbar und wird
          beim Speichern unverändert mitgeschickt (genau das erwartet der Server). */}
      <div className="kang__feld">
        <label htmlFor={`${praefix}-amount`} className="kang__label">
          {nettobetragLabel(hatPositionen)}
        </label>
        <input id={`${praefix}-amount`} className="kang__eingabe" type="text"
          inputMode="decimal" value={entwurf.netAmount} onChange={setze("netAmount")}
          readOnly={hatPositionen}
          aria-readonly={hatPositionen} />
      </div>

      <div className="kang__feld">
        <label htmlFor={`${praefix}-contact`} className="kang__label">Ansprechpartner</label>
        <select id={`${praefix}-contact`} className="kang__select"
          value={entwurf.contactId} onChange={setze("contactId")}>
          <option value="">— keiner —</option>
          {umfeld.kontakte.map((k) => (
            <option key={k.id} value={k.id}>{k.anzeigename}</option>
          ))}
        </select>
      </div>

      <div className="kang__feld">
        <label htmlFor={`${praefix}-user`} className="kang__label">Verantwortlich</label>
        <select id={`${praefix}-user`} className="kang__select"
          value={entwurf.responsibleUserId} onChange={setze("responsibleUserId")}>
          <option value="">— niemand —</option>
          {umfeld.benutzer.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      <div className="kang__feld">
        <label htmlFor={`${praefix}-valid`} className="kang__label">Gültig bis</label>
        <input id={`${praefix}-valid`} className="kang__eingabe" type="date"
          value={entwurf.validUntil} onChange={setze("validUntil")} />
      </div>

      {/* DIE LAUFZEIT (Migration 031, Entscheid E1): vier Dauern, jede als Zahl PLUS
          Einheit. Die Vertragslaufzeit steht zuerst — sie ist die Aussage, die ein Kunde
          als erste sucht, und die Spec nennt sie ausdrücklich als erstes Feld.
          Zahl und Einheit stehen NEBENEINANDER in einem Feldpaar: Zwei Zeilen wären zwei
          Angaben, und eine Einheit ohne ihre Zahl ist keine. */}
      <div className="kang__feld kang__feld--paar">
        <label htmlFor={`${praefix}-term`} className="kang__label">Vertragslaufzeit</label>
        <div className="kang__paar">
          <input id={`${praefix}-term`} className="kang__eingabe kang__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.termValue}
            onChange={setze("termValue")} />
          <select id={`${praefix}-term-unit`} className="kang__select"
            aria-label="Einheit der Vertragslaufzeit"
            value={entwurf.termUnit} onChange={setze("termUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      <div className="kang__feld kang__feld--paar">
        <label htmlFor={`${praefix}-minterm`} className="kang__label">Mindestlaufzeit</label>
        <div className="kang__paar">
          <input id={`${praefix}-minterm`} className="kang__eingabe kang__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.minimumTermValue}
            onChange={setze("minimumTermValue")} />
          <select id={`${praefix}-minterm-unit`} className="kang__select"
            aria-label="Einheit der Mindestlaufzeit"
            value={entwurf.minimumTermUnit} onChange={setze("minimumTermUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      <div className="kang__feld kang__feld--paar">
        <label htmlFor={`${praefix}-renewal`} className="kang__label">Verlängerung</label>
        <div className="kang__paar">
          <input id={`${praefix}-renewal`} className="kang__eingabe kang__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.renewalPeriodValue}
            onChange={setze("renewalPeriodValue")} />
          <select id={`${praefix}-renewal-unit`} className="kang__select"
            aria-label="Einheit der Verlängerung"
            value={entwurf.renewalPeriodUnit} onChange={setze("renewalPeriodUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      <div className="kang__feld kang__feld--paar">
        <label htmlFor={`${praefix}-notice`} className="kang__label">Kündigungsfrist</label>
        <div className="kang__paar">
          <input id={`${praefix}-notice`} className="kang__eingabe kang__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.noticePeriodValue}
            onChange={setze("noticePeriodValue")} />
          <select id={`${praefix}-notice-unit`} className="kang__select"
            aria-label="Einheit der Kündigungsfrist"
            value={entwurf.noticePeriodUnit} onChange={setze("noticePeriodUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      {/* Der Bezugspunkt „Kündigung wirkt" bleibt UNVERÄNDERT an seiner Stelle hinter der
          Frist — er ist kein eigenes Wert/Einheit-Paar, sondern die Wahl, worauf sich die
          Frist bezieht. */}
      <div className="kang__feld kang__feld--breit">
        <label htmlFor={`${praefix}-notice-ref`} className="kang__label">
          Kündigung wirkt
        </label>
        <select id={`${praefix}-notice-ref`} className="kang__select"
          value={entwurf.noticeReference} onChange={setze("noticeReference")}>
          <option value="TERM_END">zum Ende der Laufzeit</option>
          <option value="CALENDAR_YEAR_END">zum Ende des Kalenderjahres</option>
        </select>
      </div>

      {/* Die Periodenregel des Zahlplans (Migration 026) — sie gilt für den ganzen
          Beleg, deshalb steht sie am Kopf und nicht je Rate im Abschnitt darunter. */}
      <div className="kang__feld kang__feld--breit">
        <label htmlFor={`${praefix}-periodbasis`} className="kang__label">
          Leistungszeitraum
        </label>
        <select id={`${praefix}-periodbasis`} className="kang__select"
          value={entwurf.periodBasis} onChange={setze("periodBasis")}>
          <option value="CALENDAR_YEAR">Kalenderjahr</option>
          <option value="CONTRACT_YEAR">Vertragsjahr</option>
        </select>
        <p className="kang__hinweis">
          Bei Kalenderjahr zählen erstes und letztes Jahr VOLL — Beträge und Rechnungen
          werden für das ganze Kalenderjahr aufgenommen.
        </p>
      </div>
    </div>
  );
}

export type AngebotDetailProps = {
  client: AngebotDetailClient;
  kundeId: string;
  /** Der Vorgang — fertig geladen, samt seiner `version` (siehe Kopf der Datei). */
  angebot: Angebot;
  /** Die Unternehmen mit Kundenbeziehung — für das Kürzel und die Platzhalter-Vorschau. */
  companies: Unternehmen[];
  /** Die vom Rahmen geladenen Auswahllisten (`useAngebotsUmfeld`). */
  umfeld: AngebotsUmfeld;
  /** Steht GENAU DIESER Vorgang gerade in Bearbeitung? Die Entscheidung gehört dem Rahmen —
   *  nur so bleibt es bei einem offenen Formular je Ansicht. */
  bearbeitung: boolean;
  /** Ist die Bedienung gesperrt, weil anderswo ein Formular offen ist? */
  gesperrt: boolean;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`). */
  kundeName?: string | undefined;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  /** Der Rahmen soll seine Liste neu holen (nach Statuswechsel, Speichern, 409). */
  onNeuLaden: () => void | Promise<unknown>;
  /** Der Meldungsstreifen gehört dem Rahmen — er steht über der ganzen Ansicht, nicht am
   *  einzelnen Vorgang. */
  onHinweis: (text: string | null) => void;
  /** Der Vorgang ist weitergedreht (Version/Betrag aus dem Positions-Editor, frischer Kopf
   *  nach „Vorlage anwenden"): Der Rahmen schreibt ihn in seine Liste fort und reicht ihn
   *  als `angebot` zurück herein. Ohne das rechneten die Statusknöpfe mit einer veralteten
   *  Version und meldeten „zwischenzeitlich geändert" für die eigene Änderung. */
  onFortschreiben: (angebot: Angebot) => void;
  /**
   * Der Vorgang ist durch eine ANDERE Kennung ERSETZT worden — „Neue Version" oder
   * „Kopieren" (Paket Angebotsversionen, Fixrunde 1 zu Aufgabe 6). Anders als
   * `onFortschreiben` (derselbe Vorgang, neuer Stand) wechselt hier die Kennung selbst:
   * Der Rahmen muss seinen Bezug — und, wo es eine Adresse gibt, sie selbst — auf den
   * NEUEN Vorgang umstellen und seine Liste nachziehen (Global Constraint „der Rahmen
   * lädt seine Liste neu und öffnet die neue Version"). Ein blosses `onNeuLaden` griffe
   * daneben: Es liest die Kennung, die der Rahmen GERADE führt — und das ist noch die
   * alte, jetzt abgelöste.
   */
  onErsetzt: (angebot: Angebot) => void;
  /** Die Angebot→Auftrag-Naht (Ruling P3A-B): am ACCEPTED-Angebot ruft „Auftrag anlegen"
   *  ihn mit den Angebotsdaten — der Baustein erzeugt selbst KEINEN Auftrag. */
  onAuftragAusAngebot: (angebot: Angebot) => void;
};

/**
 * Das Präfix der Feld-Kennungen dieser Maske — einmal benannt, weil daraus auch das Ziel
 * des Auto-Aufklappens gebildet wird (`kopf.zeigeFeld`). Zweimal getippt wäre es beim
 * nächsten Umbenennen einmal falsch, und dann klappte der Kopfblock auf ein Feld auf, das
 * es nicht gibt.
 */
const BEARBEITEN_PRAEFIX = "kang-bearbeiten";

export function AngebotDetail(p: AngebotDetailProps): ReactElement {
  const { client, kundeId, angebot, companies, umfeld, bearbeitung, gesperrt } = p;

  const [entwurf, setEntwurf] = useState<AngebotEntwurf>(() => angebotEntwurfAus(angebot));
  /**
   * Die Einfaltung der Kopffelder: Beim BEARBEITEN eines bestehenden Vorgangs beginnt sie
   * ZU (Entscheid Dirk 08.09.2026) — Titel, Laufzeit und Kündigungsfrist stehen dann meist
   * schon, und die Arbeit findet an den Positionen statt, die dadurch nach oben rücken.
   */
  const kopf = useKopffaltung(false);
  /** Die Einfaltung des LESENDEN Zahlungsvereinbarung-Kastens im geschlossenen Detail
   *  (Gesamtreview, I2) — VORGABE geschlossen: Ohne sie riefe jede Zeile der Liste
   *  sofort `getZahlplan` ab, obwohl kaum je jemand hinsieht. */
  const zahlplanFaltung = useKopffaltung(false);
  /** Die Einfaltung des LESENDEN Kastens „Anhänge" im geschlossenen Detail — dieselbe
   *  Vorgabe geschlossen (Gesamtreview, I2): `Anhaenge` holt in seinem Effekt sofort
   *  `getAnhaenge` ab, sobald es gezeichnet wird — bei vielen Angeboten in der Liste wäre
   *  das ein Abruf je Zeile, den fast niemand ansieht. */
  const anhaengeFaltung = useKopffaltung(false);
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  /** Der Positionsstand des Vorgangs — der Editor meldet ihn nach jedem Schritt. Er
   *  entscheidet, ob der Nettobetrag von Hand änderbar ist. */
  const [positionen, setPositionen] = useState<PositionsStand | null>(null);
  /** Welche Vorlage der Anwenden-Dialog gerade anbietet (null = zu). */
  const [anwendenWahl, setAnwendenWahl] = useState<string | null>(null);
  /** Zählt hoch, sobald eine Vorlage angewandt wurde: Er steckt im `key` des Editors und
   *  hängt ihn damit neu auf, sodass er die angehängten Zeilen frisch lädt. Der Editor führt
   *  seinen Stand selbst — ohne das zeigte er nach dem Anwenden die alte Liste. */
  const [positionenNeuLaden, setPositionenNeuLaden] = useState(0);
  /** Läuft gerade eine Aktion an DIESEM Vorgang (sperrt seine Knöpfe)? */
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  /** Die Rückmeldung des „Als PDF"-Knopfes — MIT ihrer Art (siehe PDF_MELDUNGS_STIL). */
  const [pdfMeldung, setPdfMeldung] = useState<PdfMeldung | null>(null);
  /** Ist die Vorschau offen? Sie hält keinen eigenen Stand über das Schliessen hinaus —
   *  ein `boolean` genügt, die Bilder holt sie sich selbst (Spec). */
  const [vorschauOffen, setVorschauOffen] = useState(false);
  /**
   * Der Ablagestand DIESES Angebots: `undefined` heisst „noch nicht beantwortet",
   * `null` „es gibt keine". Dieselbe Dreiteilung wie an der Rechnung
   * (`RechnungDetail`) — ein blosses `null` für beides hiesse, dass die Maske beim
   * Aufschlagen „nicht abgelegt" behauptet, bevor sie gefragt hat.
   */
  const [ablage, setAblage] = useState<BelegAblage | null | undefined>(undefined);
  /** Der Entwurf des Kopfblocks PARTEIEN — Bauart und Begründung bei `useParteienGriff`
   *  (vorgangsdetail.ts). */
  const parteien = useParteienGriff();
  /** Der Entwurf des Abschnitts ZAHLUNGSVEREINBARUNG — dieselbe Bauart, DRITTER
   *  Speicherweg (`useZahlplanGriff`, vorgangsdetail.ts). */
  const zahlplan = useZahlplanGriff();

  /** Die Versionen der Reihe für den Abschnitt „Versionen" — `null` heisst „noch nicht
   *  geladen". Geladen wird NUR, wenn es überhaupt mehr als eine gibt (`versionCount`):
   *  Der Regelfall ist ein Angebot mit genau einer Fassung, und ein Abruf je
   *  Listenzeile dafür wäre reine Last. */
  const [versionen, setVersionen] = useState<AngebotVersion[] | null>(null);

  /**
   * Kein UI-Zustand überlebt den Wechsel der Angebotskennung: Der Effekt setzt beim
   * Wechsel ZUERST zurück und zählt beim Abräumen hoch (`lauf`-Ref wie in
   * `AdressListe.tsx`), damit eine spät eintreffende Antwort des VORIGEN Angebots die
   * Liste des jetzigen nicht überschreibt.
   */
  const versionsLauf = useRef(0);
  useEffect(() => {
    versionsLauf.current += 1;
    const lauf = versionsLauf.current;
    setVersionen(null);
    if (angebot.versionCount < 2) return;
    client.listOfferVersions(kundeId, angebot.id)
      .then(({ items }) => { if (versionsLauf.current === lauf) setVersionen(items); })
      .catch(() => { /* Nebenauskunft — kein Aufhalten des Details */ });
    return () => { versionsLauf.current += 1; };
  }, [client, kundeId, angebot.id, angebot.versionCount]);

  /**
   * Der Ablagestand des aufgeschlagenen Angebots. Nur an einem Angebot, das WIRKLICH
   * hinausgegangen ist — Massstab ist `sentOn`, nicht der Status (Schlussrunde I1): Ein
   * Angebot, das aus dem Entwurf direkt zurückgezogen wurde (DRAFT → WITHDRAWN), hat kein
   * `sentOn` und trotzdem einen Status ungleich DRAFT; ohne diese Prüfung erschiene dort
   * fälschlich „Noch nicht in der Akte abgelegt", obwohl der Server das Ablegen mit 422
   * abweist. Am Entwurf UND an einem nie gesendeten Angebot gibt es keine Ablage, und ein
   * Abruf wäre eine Frage mit bekannter Antwort.
   *
   * Ein Ladefehler bleibt UNBEKANNT (`undefined`) und wird nicht zur Meldung: Der
   * Ablagestand ist eine Randauskunft, und eine Fehlerzeile dafür stünde neben dem
   * Beleg, um den es geht.
   */
  const ablageLesen = useCallback(async (
    id: string, status: OfferStatus, sentOn: string | null,
  ): Promise<void> => {
    if (status === "DRAFT" || sentOn === null) { setAblage(undefined); return; }
    try {
      const { ablage: stand } = await client.getOfferAblage(kundeId, id);
      setAblage(stand);
    } catch {
      setAblage(undefined);
    }
  }, [client, kundeId]);

  useEffect(() => {
    void ablageLesen(angebot.id, angebot.status, angebot.sentOn);
    // `status` UND `sentOn` stehen in den Abhängigkeiten: `status` ändert sich beim
    // Versand (dann gibt es plötzlich eine Ablage), `sentOn` unterscheidet ein gesendetes
    // von einem nie gesendeten, zurückgezogenen Angebot bei GLEICHEM Status (WITHDRAWN).
  }, [angebot.id, angebot.status, angebot.sentOn, ablageLesen]);

  /**
   * Der Übergang „schliesst auf": Beim Öffnen der Bearbeitung wird der Entwurf frisch aus
   * dem Vorgang gebildet, und der Stand des vorigen Anlaufs verfällt.
   *
   * Bewusst WÄHREND des Zeichnens statt in einem Effekt (React: „Adjusting state when a
   * prop changes"): Ein Effekt liefe erst nach dem ersten Bild, und für einen Augenblick
   * stünden die Werte des letzten Anlaufs im Formular. `setPositionen(null)` gehört dazu —
   * sonst spränge der Nettobetrag in die Nur-Lese-Fassung, bevor der Editor geladen hat.
   */
  const [zuletztBearbeitung, setZuletztBearbeitung] = useState(bearbeitung);
  if (bearbeitung !== zuletztBearbeitung) {
    setZuletztBearbeitung(bearbeitung);
    if (bearbeitung) {
      setEntwurf(angebotEntwurfAus(angebot));
      setFeldFehler(null);
      setAnwendenWahl(null);
      setPositionen(null);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      // Und die Einfaltung mit: Wer ein Panel neu aufschlägt, findet den Kopfblock im
      // Startzustand vor — nicht so, wie er ihn beim vorigen Vorgang hinterlassen hat.
      kopf.zuruecksetzen();
      // Die Vorschau schliesst, wenn die Bearbeitung von AUSSEN beginnt (M5, Schlussrunde):
      // Ihr Schleier liegt bei z-index 60, das Vorschau-Modal bei 100 — ohne diese Zeile
      // läge das Bearbeiten-Panel HINTER der Vorschau, statt sie abzulösen.
      setVorschauOffen(false);
    }
  }

  /**
   * Der Editor meldet nach jedem Schritt die frische Version des VORGANGS und den ganzen
   * Stand. Beides muss ankommen: Die Version geht in das nächste `updateOffer` (sonst 409),
   * und aus dem Stand liest die Maske den berechneten Betrag.
   *
   * Und zwar bis in die LISTE des Rahmens (`onFortschreiben`). Die Liste zu übergehen war
   * ein Fehler (Fund der E2E-Aufgabe 6): Die Statusknöpfe und die Angebot→Auftrag-Naht
   * arbeiten mit der Version AUS DER LISTE. Wer Positionen bearbeitete und das Formular
   * dann mit „Abbrechen" schloss, bekam beim ersten Klick auf „Senden" eine 409 samt der
   * Meldung „Dieses Angebot wurde zwischenzeitlich geändert" — obwohl niemand dazwischenkam.
   */
  function positionenGeaendert(version: number, stand: PositionsStand): void {
    setPositionen(stand);
    p.onFortschreiben({ ...angebot, version, netAmount: stand.netAmount });
    setEntwurf((v) => (
      // Sobald Positionen da sind, ist der Betrag berechnet — das Anzeigefeld führt genau
      // den Wert, den `updateOffer` unverändert zurückerwartet (sonst 422 BETRAG_BERECHNET).
      stand.items.length === 0 ? v : { ...v, netAmount: betragText(stand.netAmount) }));
  }

  /** Hat der Vorgang Positionen? Entschieden an der ANZAHL, nicht am Betrag: Ein Beleg aus
   *  lauter Zugaben zu 0,00 hat Positionen (Fund G1 aus Aufgabe 2). */
  const hatPositionen = positionen !== null && positionen.items.length > 0;

  /**
   * „Vorlage anwenden" am BESTEHENDEN Angebot: Die Positionen der Vorlage werden ANGEHÄNGT,
   * Einleitungs- und Ausleitungstext übernimmt der Server nur, wenn sie leer sind.
   *
   * Danach wird der Kopf FRISCH gelesen. Das ist kein Beiwerk: Der Entwurf im Formular
   * kennt die Texte von vorher. Schickte ihn das nächste Speichern unverändert, machte es
   * die Übernahme des Servers still rückgängig (oder überschriebe einen bestehenden Text
   * mit dem alten Stand) — die Zusage „nur wenn leer" gälte dann nur bis zum nächsten Klick.
   */
  async function vorlageAnwenden(vorlageId: string): Promise<void> {
    if (vorlageId === "") { setFeldFehler("Bitte eine Vorlage wählen."); return; }
    setSpeichert(true);
    setFeldFehler(null);
    try {
      const stand = await client.applyTemplate(
        kundeId, angebot.id, angebot.version, vorlageId);
      // Der Editor führt seinen Stand selbst — der Zähler im `key` hängt ihn neu auf, damit
      // er die angehängten Zeilen lädt. `positionenGeaendert` allein reichte nicht.
      setPositionenNeuLaden((n) => n + 1);
      setPositionen(stand);
      const frisch = await client.getOffer(kundeId, angebot.id);
      // Und in die LISTE, geradeaus statt über Umwege: Die Statusknöpfe und die
      // Angebot→Auftrag-Naht rechnen mit der Version aus der Liste. Bisher wurde sie nur
      // INDIREKT geheilt — der neu aufgehängte Editor meldet beim Laden nach oben. Der
      // direkte Weg hier hängt zwar weiter an genau diesem GET, macht die Heilung aber vom
      // Editor-Neuaufbau unabhängig.
      p.onFortschreiben(frisch);
      setEntwurf(angebotEntwurfAus(frisch));
      setAnwendenWahl(null);
      p.onHinweis("Die Vorlage ist angewandt — ihre Positionen stehen am Ende der Liste.");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieses Angebot wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen — bitte erneut anwenden.");
        p.onSchliessen();
        await p.onNeuLaden();
      } else {
        setFeldFehler(apiMeldung(e, "Die Vorlage konnte nicht angewandt werden."));
        // Auch der SERVER kann Kopffelder bemängeln (das Anwenden schreibt den Kopf mit):
        // Nennt er eines, das im eingeklappten Block steht, wird es sichtbar gemacht.
        zeigeAngebotsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, e);
      }
    } finally {
      setSpeichert(false);
    }
  }

  /** Der zweite Speicherweg im selben Klick — Reihenfolge, Version und Sinn stehen an
   *  `speichereParteien` (vorgangsdetail.ts), damit alle drei Beleg-Masken EINE Naht
   *  haben. `null` heisst: Der Kopf steht, die Parteien nicht; das Panel bleibt offen.
   *  Sonst die Version, die der Beleg NACH diesem Weg trägt — der DRITTE Weg
   *  (Zahlplan) rechnet mit ihr weiter. */
  function parteienSpeichern(frisch: Angebot): Promise<number | null> {
    let version: number | null = null;
    return speichereParteien({
      client, art: "offer", customerId: angebot.customerId, vorgangId: angebot.id,
      version: frisch.version, griff: parteien,
      beiFehler: (meldung, fehler) => {
        // Der frische Vorgang muss in die Liste des Rahmens und in den Entwurf — sonst
        // rechnete der nächste Anlauf mit der alten Version und bekäme eine 409 über eine
        // Änderung, die niemand fremd gemacht hat.
        p.onFortschreiben(frisch);
        setEntwurf(angebotEntwurfAus(frisch));
        setFeldFehler(meldung);
        zeigeAngebotsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, fehler);
      },
      beiErfolg: (neueVersion) => { version = neueVersion; },
    }).then((ok) => (ok ? version ?? frisch.version : null));
  }

  /** Der DRITTE Speicherweg — die Zahlungsvereinbarung. Reihenfolge, Version und Sinn
   *  stehen an `speichereZahlplan` (vorgangsdetail.ts): Kopf → Parteien → Zahlplan, und
   *  die Version ist die aus der ANTWORT des Parteien-Weges. */
  function zahlplanSpeichern(vorgangId: string, version: number): Promise<boolean> {
    return speichereZahlplan({
      client, bezug: { art: "offer", customerId: angebot.customerId, vorgangId },
      version, griff: zahlplan,
      beiFehler: (meldung, fehler) => {
        setFeldFehler(meldung);
        zeigeAngebotsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, fehler);
      },
    });
  }

  async function speichern(): Promise<void> {
    const problem = pruefeAngebot(entwurf, false);
    if (problem !== null) {
      setFeldFehler(problem.meldung);
      // Der Befund betrifft immer ein Feld im Kopfblock — der beim Bearbeiten zugeklappt
      // beginnt. Ohne dieses Aufklappen stünde die Meldung über einem Formular, in dem das
      // gemeinte Feld gar nicht zu sehen ist: ein unsichtbarer Fehler.
      zeigeAngebotsFeld(kopf, BEARBEITEN_PRAEFIX, problem.feld);
      return;
    }
    setSpeichert(true);
    setFeldFehler(null);
    try {
      // version = die gelesene Version: ein zwischenzeitlich fremd geänderter Satz ergibt 409
      // statt einer stillen Überschreibung.
      //
      // BINDEND FÜR VERWENDER: Die Liste darf nicht neu laden, solange ein Detail in
      // Bearbeitung ist — sonst würde eine Fremdänderung still überschrieben statt 409.
      // Die Version, die hier hinausgeht, ist die aus der Liste des Rahmens (`angebot`);
      // ein Neuladen während der Bearbeitung ersetzte sie unbemerkt durch die fremde und
      // machte damit genau die optimistische Sperre wirkungslos, die diese Zeile aufruft.
      const frisch = await client.updateOffer(
        kundeId, angebot.id, angebot.version, angebotKopffelder(entwurf));
      // Kopf → Parteien → Zahlplan. Jeder Weg dreht die Belegversion weiter; der nächste
      // bekommt die aus der ANTWORT des vorigen — der Zahlplan steht zuletzt, weil er
      // der einzige Weg ist, der die geplanten Fälligkeiten neu rechnet.
      const parteienVersion = await parteienSpeichern(frisch);
      if (parteienVersion === null) return;
      if (!await zahlplanSpeichern(angebot.id, parteienVersion)) return;
      p.onSchliessen();
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieses Angebot wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen — bitte erneut bearbeiten.");
        p.onSchliessen();
        await p.onNeuLaden();
      } else {
        setFeldFehler(apiMeldung(e, "Die Änderung konnte nicht gespeichert werden."));
        // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske
        // (Titel-Höchstlänge, Betragsgrenzen, Halbe-Kündigungsfrist, BETRAG_BERECHNET …).
        // Ohne diese Zeile bliebe sein Befund über einem eingeklappten Feld stehen.
        zeigeAngebotsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, e);
      }
    } finally {
      setSpeichert(false);
    }
  }

  async function statusSetzen(status: OfferStatus): Promise<void> {
    setBeschaeftigt(true);
    p.onHinweis(null);
    // Wie an `alsPdf`/`inAkteAblegen`: eine ALTE PDF-Quittung ("wurde heruntergeladen …")
    // gehört zu einem vorigen Klick und nicht zu diesem Statuswechsel — sie bliebe sonst
    // stehen, bis (falls überhaupt) eine neue Meldung sie ersetzt (M6, Schlussrunde).
    setPdfMeldung(null);
    try {
      const neu = await client.setOfferStatus(kundeId, angebot.id, angebot.version, status);
      // Die Ablage-Auskunft des Statuswechsels (Spec Belegvorschau): Beim Versand legt
      // der Server das endgültige PDF in die Kundenakte. Gelingt es, steht der Stand
      // hier; scheitert es, ist der Status TROTZDEM gesetzt, und `ablageHinweis` sagt,
      // wie es nachzuholen ist. Beides wird in DIESELBE Meldungsstelle geschrieben wie
      // die PDF-Wege — eine zweite Zeile für dieselbe Sorte Auskunft wäre eine Stelle,
      // die man beim nächsten Fix vergisst.
      //
      // `neu.ablage === null` ist zweideutig (M1, Schlussrunde): An ACCEPTED/WITHDRAWN
      // versucht der Server gar nicht erst abzulegen (`ablageNachVersand` legt NUR bei
      // SENT ab) — dort heisst `null` "nichts versucht", nicht "nichts da", und eine
      // schon geladene Ablage darf stehen bleiben. Nur am WIRKLICHEN Versand (`status
      // === "SENT"`) ist `null` eine echte Auskunft ("versucht, nichts da") und wird
      // übernommen.
      if (neu.ablage !== null || status === "SENT") setAblage(neu.ablage);
      if (neu.ablageHinweis !== null) {
        setPdfMeldung({ text: neu.ablageHinweis, art: "schlecht" });
      } else if (neu.ablage !== null) {
        setPdfMeldung({
          text: `Das PDF liegt als „${neu.ablage.fileName}" in der Kundenakte.`,
          art: "gut",
        });
      }
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieses Angebot wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen.");
        await p.onNeuLaden();
      } else {
        p.onHinweis(apiMeldung(e, "Der Status konnte nicht geändert werden."));
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „PDF in Akte ablegen" — das Nachholen, wenn die Automatik beim Versand nicht kam
   * (oder das Erneuern nach einer Änderung).
   *
   * IDEMPOTENT: Liegt die Datei schon, wird ihr Inhalt ersetzt — dieselbe Kennung, kein
   * zweiter Eintrag, und ein vom Bearbeiter geänderter Name bleibt stehen.
   */
  async function inAkteAblegen(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const stand = await client.createOfferAblage(kundeId, angebot.id);
      setAblage(stand);
      // Derselbe Wortlaut wie am „PDF in Akte ablegen"-Knopf der Rechnung
      // (`RechnungDetail.inAkteAblegen`) — beide Belegarten sprechen hier bewusst dieselbe
      // Sprache. Die flüchtige Meldung und die DAUERHAFTE Ablage-Zeile unter den Aktionen
      // sagen zwar dasselbe, aber mit unterschiedlichem Wortlaut ("liegt JETZT" vs.
      // "ABGELEGT am") — genau deshalb lässt sich in Tests jede der beiden gezielt
      // ansprechen, ohne dass eine Suche nach dem Dateinamen beide träfe.
      setPdfMeldung({
        text: `Das PDF liegt jetzt in der Akte (${stand.fileName}) — im Reiter „Dateien".`,
        art: "gut",
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        setPdfMeldung({
          text: apiMeldung(e, "Das PDF konnte nicht in der Kundenakte abgelegt werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" (E3-T4): Die Engine setzt das Angebot (Dokumentart ANGEBOT), die API
   * liefert den Download durch, hier wird er gespeichert. Die drei Ausgänge tragen ihre
   * ART mit (PDF_MELDUNGS_STIL): Erfolg als Status, die 501 der unkonfigurierten
   * Umgebung als HINWEIS (bekannter Arbeitsstand, kein Alarm), alles andere als Alarm.
   */
  async function alsPdf(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOffer(kundeId, angebot.id);
      const name = filename ?? "angebot.pdf";
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        // Nennt die Engine Feldpfade, reisen sie strukturiert mit (`fields[]`, an JEDEM
        // Engine-Fehlschlag) und werden unten als Liste gezeigt. Der Schlüssel entfällt,
        // wenn keine kamen — `exactOptionalPropertyTypes`.
        setPdfMeldung({
          text: apiMeldung(e, "Das Angebot konnte nicht als PDF ausgegeben werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Neue Version": eine Rückfrage, dann der Aufruf — und dann meldet das Detail dem
   * RAHMEN die NEUE Kennung (`onErsetzt`), statt sie hier weiterzuführen oder den Rahmen
   * bloss zum Neuladen zu bitten.
   *
   * Review-Befund Fixrunde 1: Ein `onNeuLaden` allein liefe mit der ALTEN Kennung —
   * `onNeuLaden` liest genau den Vorgang, den der Rahmen gerade führt, und das ist noch
   * der abgelöste. Es läse also die jetzt SUPERSEDED Fassung zurück und überschriebe die
   * eben angelegte neue Version sofort wieder. `onErsetzt` gibt dem Rahmen deshalb den
   * NEUEN Satz direkt mit — er muss ihn nicht ein zweites Mal vom Server holen — und mit
   * ihm auch dessen Kennung, damit der Rahmen seinen Bezug (und, wo es eine Adresse gibt,
   * sie selbst) umstellt und seine Liste nachzieht.
   */
  async function neueVersion(vorlageOfferId: string | null = null): Promise<void> {
    if (!window.confirm(vorlageOfferId === null
      ? FRAGE_NEUE_VERSION : FRAGE_WIEDERHERSTELLEN)) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      const neu = await client.neueOfferVersion(kundeId, angebot.id, vorlageOfferId);
      p.onHinweis(`Version ${neu.versionNumber} wurde angelegt. Die bisherige Fassung `
        + "ist jetzt veraltet.");
      p.onErsetzt(neu);
    } catch (e) {
      p.onHinweis(apiMeldung(e, "Die neue Version konnte nicht angelegt werden."));
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" AN EINER BESTIMMTEN VERSION — der Weg aus dem Versionsabschnitt. Er ruft
   * denselben Rendern-Weg wie `alsPdf`, nur mit der Kennung der gewählten Version, und
   * meldet in dieselbe Rückmeldung (`pdfMeldung`). Eine zweite Meldungsstelle je Zeile
   * wäre ein zweiter Ort für dieselbe Auskunft.
   */
  async function alsPdfVon(v: AngebotVersion): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOffer(kundeId, v.id);
      const name = filename ?? `angebot-v${String(v.versionNumber)}.pdf`;
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      setPdfMeldung({
        text: apiMeldung(e, "Diese Version konnte nicht als PDF ausgegeben werden."),
        art: e instanceof ApiError && e.status === 501 ? "hinweis" : "schlecht",
      });
    } finally {
      setBeschaeftigt(false);
    }
  }

  /** „Kopieren": dieselbe Bauart, anderes Ergebnis — ein Angebot mit EIGENER Nummer und
   *  eigener Kennung. Die Meldung nennt sie, weil genau sie der Unterschied zur neuen
   *  Version ist. Wie bei `neueVersion` meldet das Detail dem Rahmen die neue Kennung
   *  über `onErsetzt` — ohne das bliebe die Kopie unsichtbar: Sie steht unter einer
   *  ANDEREN Kennung als der gerade gezeigte Vorgang, und ein blosses „bitte neu laden"
   *  läse weiterhin nur diesen. */
  async function kopieren(): Promise<void> {
    if (!window.confirm(FRAGE_KOPIEREN)) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      const kopie = await client.kopiereOffer(kundeId, angebot.id);
      p.onHinweis(`Das Angebot wurde als ${kopie.offerNumber} kopiert.`);
      p.onErsetzt(kopie);
    } catch (e) {
      p.onHinweis(apiMeldung(e, "Das Angebot konnte nicht kopiert werden."));
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * Trägt das Panel ungespeicherte Eingaben? Der Kopfblock PARTEIEN zählt mit: Er meldet
   * seinen Entwurf erst, sobald ihn jemand angefasst hat — und genau dann gibt es etwas zu
   * verlieren.
   */
  const ungespeichert = bearbeitung
    && (!entwuerfeGleich(entwurf, angebotEntwurfAus(angebot))
      || parteien.beruehrt || zahlplan.beruehrt);

  /* Der Wächter gegen das Schliessen des BROWSERS (Global Constraint „kein stiller
     Datenverlust"): Der Abbrechen-Wächter fängt nur den Weg über den Knopf und Escape;
     ein geschlossener Reiter ginge an ihm vorbei. */
  useBeforeUnload(ungespeichert);

  const folgestatus = erlaubteFolgestatus(angebot.status);
  const kuerzel = companies.find((c) => c.id === angebot.companyId)?.shortCode ?? null;

  /**
   * Der EINE Ausgang des Bearbeitungspanels neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei
   * ungespeicherten Kopfeingaben. Knopf und Escape gehen durch diese Funktion.
   *
   * Verglichen wird gegen den Vorgang, wie er GERADE steht — nicht gegen einen zweiten,
   * mitgeführten Ausgangsstand: Ein Schritt im Positions-Editor dreht die Version weiter und
   * schreibt den Betrag ins Feld zurück (`positionenGeaendert`), und der Vorgang kommt über
   * `onFortschreiben` frisch wieder herein. Ein eingefrorener Ausgangsstand meldete danach
   * eine „Änderung", die längst gespeichert ist.
   *
   * Die POSITIONEN selbst stehen nie ungespeichert im Panel — der Editor schreibt jeden
   * Schritt sofort. Die Rückfrage kann sie deshalb übergehen, ohne etwas zu verschweigen.
   */
  function abbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(ungespeichert)) return;
    parteien.zuruecksetzen();
    zahlplan.zuruecksetzen();
    p.onSchliessen();
  }

  return (
    <>
      <div className="kang__zeile">
        <span className="kang__nummer">{angebot.offerNumber}</span>
        {/* Die Versionspille steht NEBEN der Nummer, nicht IN ihr (Festlegung V4):
            Die Nummer bleibt die Nummer, auch beim Kopieren in die Zwischenablage. */}
        {versionsZusatz(angebot.versionNumber) !== "" && (
          <span className="kang__version">{versionsZusatz(angebot.versionNumber)}</span>
        )}
        {kuerzel !== null && <span className="kang__kuerzel">{kuerzel}</span>}
        <span className="kang__eintrag-titel">{angebot.title}</span>
        <span className={`kang__status kang__status--${angebot.status.toLowerCase()}`}>
          {ANGEBOT_STATUS_LABEL[angebot.status]}
        </span>
      </div>

      {/* Die abgelöste Version sagt es SELBST und nennt, wodurch: Ohne diesen Satz
          stünde ein Bearbeiter vor einem Angebot ohne Bearbeiten-Knopf und wüsste
          nicht, warum. Die Ordnungszahl kommt aus dem geladenen Versionsabschnitt
          (`versionen[0]` — jüngste zuerst): `angebot.versionCount` ist der Stand BEIM
          LESEN dieser (abgelösten) Zeile und kann inzwischen veraltet sein, wenn seither
          eine weitere Version entstand. Solange die Liste noch lädt, bleibt `versionCount`
          der einzig verfügbare Rückfall (Fixrunde nach Gesamtreview, M1). */}
      {!angebot.istJuengste && (
        <p className="kang__veraltet" role="status">
          Veraltet, ersetzt durch {versionsZusatz(versionen?.[0]?.versionNumber
            ?? angebot.versionCount)}
        </p>
      )}

      <div className="kang__daten">
        <span className="kang__betrag ki-num">
          {formatBetrag(angebot.netAmount, angebot.currency)}
        </span>
        {angebot.validUntil !== null && (
          <span className="kang__gueltig">
            gültig bis {formatDatum(angebot.validUntil)}
          </span>
        )}
        {angebot.contactName !== null && angebot.contactName !== "" && (
          <span className="kang__kontakt">{angebot.contactName}</span>
        )}
      </div>

      {/* Die PDF-Rückmeldung steht AM VORGANG, zu dem sie gehört: Der Baustein trägt sie
          samt ihrer Befundliste selbst, damit sie auch dort erscheint, wo kein Reiter
          darüber liegt (der Angebots-Bereich aus Aufgabe 3). */}
      {pdfMeldung !== null && (
        <p className={PDF_MELDUNGS_STIL[pdfMeldung.art].klasse}
          role={PDF_MELDUNGS_STIL[pdfMeldung.art].rolle}>
          {pdfMeldung.text}
        </p>
      )}
      {/* Die Feldbefunde als LISTE — jeder Pfad einzeln und ungekürzt, zum Abhaken.
          NEBEN der Meldung und nicht in ihr: Die trägt `role="alert"` und muss ein `<p>`
          bleiben (ein `<ul>` darf dort gar nicht hinein), und die Ansage soll ein SATZ
          sein, keine vorgelesene Liste.

          EIGENER Blockname `kang__` und kein geteilter Block mit den Rechnungen: Dieselbe
          Klasse über zwei Ansichten hinweg hiesse, dass eine Stilanpassung an der einen
          die andere still mitverändert. Die Gestalt ist bewusst dieselbe, der Name nicht. */}
      {pdfMeldung?.felder !== undefined && pdfMeldung.felder.length > 0 && (
        <ul className="kang__fehlerfelder" aria-label="Fehlende Pflichtwerte">
          {pdfMeldung.felder.map((f) => (
            <li key={f.field}>
              <code>{f.field}</code>: {f.message}
            </li>
          ))}
        </ul>
      )}

      {bearbeitung && (
        <Modal titel={`Angebot ${angebot.offerNumber} bearbeiten`} breite="weit"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kang__bearbeitung">
            {angebotFormularFelder({
              praefix: BEARBEITEN_PRAEFIX, mitUnternehmen: false, companies, umfeld,
              entwurf, setEntwurf, hatPositionen, feldFehler, kopf,
              vorschauWerte: angebotVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, angebot,
                kundeName: p.kundeName,
              }),
              /* Der Kopfblock PARTEIEN — `key` am Vorgang: Ein Wechsel des bearbeiteten
                 Angebots baut ihn neu auf, statt den Entwurf des vorigen weiterzureichen.
                 Nur beim BEARBEITEN, nicht beim Anlegen: Vor dem ersten Speichern gibt es
                 keinen Beleg, dessen Parteien man setzen könnte. */
              parteien: (
                <BelegParteien key={angebot.id} client={client} art="offer"
                  customerId={angebot.customerId} vorgangId={angebot.id}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={speichert}
                  befunde={parteien.befunde} onEntwurf={parteien.melde} />
              ),
              /* Der Abschnitt ZAHLUNGSVEREINBARUNG — `key` am Vorgang aus demselben Grund
                 wie an PARTEIEN: „Neue Version"/„Kopieren" wechseln die Kennung, und ein
                 Wechsel muss den Entwurf des VORIGEN Belegs verwerfen, statt seine (jetzt
                 fremden) Ratenkennungen weiterzutragen. */
              zahlplan: (
                <Zahlplan key={`${angebot.id}-zahlplan`} client={client}
                  bezug={{ art: "offer", customerId: angebot.customerId,
                           vorgangId: angebot.id }}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={speichert}
                  befunde={zahlplan.befunde} onEntwurf={zahlplan.melde} />
              ),
              /* Der Kasten „Anhänge" — `key` am Vorgang aus demselben Grund wie an
                 PARTEIEN/ZAHLPLAN. DIESELBE Naht wie beim Positions-Editor: Jeder
                 Schreibweg des Kastens dreht die Belegversion weiter, und das nächste
                 „Speichern" muss sie kennen — sonst schickt es eine veraltete Version und
                 bekommt eine 409 für eine Änderung, die niemand gemacht hat. */
              anhaenge: (
                <Anhaenge key={`${angebot.id}-anhaenge`} client={client}
                  bezug={{ art: "offer", customerId: angebot.customerId,
                           vorgangId: angebot.id }}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={speichert}
                  // Die AKTUELLE Belegversion geht HEREIN (Review-Befund zur Fixrunde
                  // K10, 24.09.2026): Positionen und Kopf-Speichern drehen `angebot.version`
                  // im selben Dialog weiter, ohne dass der Kasten selbst neu liest — ohne
                  // diese Eigenschaft liefe sein NÄCHSTER eigener Schreibweg (Ziehen,
                  // Entfernen, Anhängen) mit einer veralteten Version in eine 409.
                  version={angebot.version}
                  onVersionChange={(version) => {
                    p.onFortschreiben({ ...angebot, version });
                  }} />
              ),
              /* Der grösste Block, in der Mitte der Maske (Entscheid Dirk 08.09.2026):
                 zwischen Einleitungs- und Ausleitungstext, in voller Breite. Die Positionen
                 hängen an DEMSELBEN Vorgang wie die Kopffelder darüber — jeder Schritt im
                 Editor dreht die Version des Angebots weiter, und `positionenGeaendert`
                 trägt sie über den Rahmen zurück, sonst liefe das nächste Speichern des
                 Kopfes in eine 409. */
              positionen: (
                <div className="kang__positionen">
                  <div className="kang__positionen-kopf">
                    <h3 className="kang__abschnitt">Positionen</h3>
                    <button type="button" className="kang__sekundaer" disabled={speichert}
                      onClick={() => setAnwendenWahl(anwendenWahl === null ? "" : null)}>
                      Vorlage anwenden
                    </button>
                  </div>

                  {/* Der Anwenden-Dialog sagt VORHER, was geschieht: Die Positionen kommen
                      ANS ENDE (nichts wird ersetzt), und die Texte übernimmt der Server nur,
                      wenn sie leer sind. Ohne diesen Satz wäre „anwenden" eine Wette darauf,
                      ob die eigene Arbeit stehen bleibt. */}
                  {anwendenWahl !== null && (
                    <div className="kang__anwenden" aria-label="Vorlage anwenden">
                      <p className="kang__vorlagen-hinweis">
                        Die Positionen der Vorlage werden an die bestehende Liste angehängt;
                        Einleitungs- und Ausleitungstext werden nur übernommen, wenn sie leer
                        sind. Periodenregel und Zahlplan bleiben unverändert — sie kommen nur
                        mit, wenn das Angebot MIT der Vorlage angelegt wird. Noch nicht
                        gespeicherte Textänderungen ersetzt dabei der Stand vom Server.
                      </p>
                      <label htmlFor="kang-anwenden-vorlage" className="kang__label">
                        Vorlage
                      </label>
                      <select id="kang-anwenden-vorlage" className="kang__select"
                        value={anwendenWahl}
                        onChange={(e) => setAnwendenWahl(e.target.value)}>
                        <option value="">Bitte wählen …</option>
                        {umfeld.vorlagen.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name} ({v.itemCount}{" "}
                            {v.itemCount === 1 ? "Position" : "Positionen"})
                          </option>
                        ))}
                      </select>
                      <div className="kang__formular-aktionen">
                        <button type="button" className="kang__primaer"
                          disabled={speichert || anwendenWahl === ""}
                          onClick={() => { void vorlageAnwenden(anwendenWahl); }}>
                          Anwenden
                        </button>
                        <button type="button" className="kang__sekundaer"
                          disabled={speichert} onClick={() => setAnwendenWahl(null)}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}

                  <PositionsEditor
                    // Der Zähler im Schlüssel hängt den Editor nach einem Anwenden neu auf —
                    // nur so lädt er die angehängten Zeilen (er führt seinen Stand selbst).
                    key={`${angebot.id}-${String(positionenNeuLaden)}`}
                    client={client}
                    bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                    companyId={angebot.companyId} version={angebot.version}
                    onVersionChange={positionenGeaendert} />
                </div>
              ),
            })}
          </div>

          {/* Die zwei einzigen Ausgänge, unten am Panel: Speichern schreibt die KOPFFELDER
              (die Positionen schreibt der Editor Schritt für Schritt selbst). */}
          <ModalFuss speichernText="Speichern" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}

      {!bearbeitung && versionen !== null && versionen.length > 1 && (
        <section className="kang__versionen" aria-label="Versionen">
          <h3 className="kang__abschnitt">Versionen</h3>
          <table className="kang__versionen-tabelle">
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Datum</th>
                <th scope="col">Status</th>
                <th scope="col">Betrag</th>
                <th scope="col">Aktionen</th>
              </tr>
            </thead>
            {/* JÜNGSTE OBEN (Hausregel „Wichtigstes oben") — der Server liefert sie
                bereits in dieser Reihenfolge; hier wird NICHT nachsortiert. */}
            <tbody>
              {versionen.map((v) => (
                <tr key={v.id}
                  className={v.id === angebot.id ? "kang__versionen-zeile--aktiv" : undefined}
                  aria-current={v.id === angebot.id ? "true" : undefined}>
                  <th scope="row">{versionsZusatz(v.versionNumber) || "v1"}</th>
                  <td>{formatDatum(v.createdOn)}</td>
                  <td>{ANGEBOT_STATUS_LABEL[v.status]}</td>
                  <td className="ki-num">{formatBetrag(v.netAmount, angebot.currency)}</td>
                  <td className="kang__versionen-aktionen">
                    {/* JEDE Version bleibt als PDF reproduzierbar — das ist der Kern
                        der Spec („Jede Version ist vollständig lesbar und als PDF
                        reproduzierbar, auch nachdem eine neuere entstanden ist"). Der
                        Rendern-Weg nimmt die Kennung DIESER Version, nicht die des
                        gezeigten Angebots. */}
                    <button type="button" className="kang__sekundaer"
                      aria-label={`Version ${v.versionNumber} als PDF`}
                      disabled={gesperrt || beschaeftigt}
                      onClick={() => { void alsPdfVon(v); }}>
                      Als PDF
                    </button>
                    {/* „Version wiederherstellen" ist keine eigene Mechanik, sondern
                        eine neue Version MIT DIESER als Vorlage — deshalb steht der
                        Knopf hier und heisst, was er tut. An der gezeigten (jüngsten)
                        Version selbst wäre er der gewöhnliche „Neue Version"-Knopf und
                        deshalb weggelassen. */}
                    {angebot.darfNeueVersion && v.id !== angebot.id && (
                      <button type="button" className="kang__sekundaer"
                        aria-label={`Version ${v.versionNumber} als Vorlage für neue Version`}
                        disabled={gesperrt || beschaeftigt}
                        onClick={() => { void neueVersion(v.id); }}>
                        Als Vorlage für neue Version
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Der Abschnitt ZAHLUNGSVEREINBARUNG im geschlossenen Detail — LESEND, die
          fertigen Sätze des Servers: Gepflegt wird der Zahlplan im Modal, hier steht nur
          das Ergebnis. Eigener Wrapper `kang__zahlplan` allein für den Abstand an diesem
          Einbauort (Kollisions-Lehre 01.09.: kein geteilter Blockname für `zpl`).
          HINTER EINEM AUFKLAPPER, geschlossen (Gesamtreview, I2): `Zahlplan` ruft in
          seinem Effekt sofort `getZahlplan` ab, sobald er gezeichnet wird — bei vielen
          Angeboten in der Liste wäre das ein Abruf je Zeile, den fast niemand ansieht.
          Erst das Öffnen hängt ihn ein. */}
      {!bearbeitung && (
        <div className="kang__zahlplan">
          <Aufklapper faltung={zahlplanFaltung} titel="Zahlungsvereinbarung"
            inhaltId="kang-zahlplan-inhalt">
            {zahlplanFaltung.offen && (
              <Zahlplan client={client}
                bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                idPraefix="kang-lese" nurLesend />
            )}
          </Aufklapper>
        </div>
      )}

      {/* Der Kasten „Anhänge" im geschlossenen Detail — LESEND, hinter EINEM eigenen
          Aufklapper (Kollisions-Lehre 01.09.: kein geteilter Blockname; eigener Wrapper
          `kang__anhaenge` allein für den Abstand an diesem Einbauort). GESCHLOSSEN und
          LAZY, aus demselben Grund wie beim Zahlplan (Gesamtreview, I2): `Anhaenge` holt
          in seinem Effekt sofort `getAnhaenge` ab, sobald es gezeichnet wird — bei vielen
          Angeboten in der Liste wäre das ein Abruf je Zeile, den fast niemand ansieht. */}
      {!bearbeitung && (
        <div className="kang__anhaenge">
          <Aufklapper faltung={anhaengeFaltung} titel="Anhänge"
            inhaltId="kang-anhaenge-inhalt">
            {anhaengeFaltung.offen && (
              <Anhaenge client={client}
                bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                idPraefix="kang-lese" nurLesend />
            )}
          </Aufklapper>
        </div>
      )}

      {!bearbeitung && (
        <div className="kang__aktionen">
          {/* Bearbeiten NUR an der jüngsten Version. Eine ältere ist lesend — der
              Server wiese sie mit 422 ANGEBOT_VERALTET ab, und ein Knopf, der das
              sicher auslöst, ist keine Bedienführung, sondern eine Falle. */}
          {angebot.istJuengste && (
            <button type="button" className="kang__sekundaer"
              aria-label={`${angebot.title} bearbeiten`}
              disabled={gesperrt || beschaeftigt}
              onClick={p.onBearbeiten}>
              Bearbeiten
            </button>
          )}
          {/* Die Vorschau — DIREKT NEBEN „Als PDF" (Spec, wörtlich): Beide zeigen dasselbe
              Druckbild, einmal auf dem Bildschirm, einmal als Datei. */}
          <button type="button" className="kang__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => setVorschauOffen(true)}>
            Vorschau
          </button>
          <button type="button" className="kang__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => { void alsPdf(); }}>
            Als PDF
          </button>
          {folgestatus.map((s) => (
            <button key={s} type="button" className="kang__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void statusSetzen(s); }}>
              {STATUS_AKTION[s]}
            </button>
          ))}
          {/* Der Satz steht NEBEN dem Knopf und nicht in ihm: Die Beschriftung soll
              kurz bleiben, die Ansage ausdrücklich sein. */}
          {folgestatus.includes("SENT") && (
            <span className="kang__statushinweis">{SENT_HINWEIS}</span>
          )}
          {/* Die Ablage in der Kundenakte NACHHOLEN oder ERNEUERN — sichtbar, sobald wir
              eine Antwort auf „liegt sie?" haben (`ablage !== undefined`). Das ist bewusst
              NICHT an `angebot.status` gebunden: Direkt nach „Als gesendet markieren" hat
              der Rahmen die frische Version des Angebots (noch) nicht nachgeladen, der
              Ablagestand aus der STATUSANTWORT ist aber schon da — und genau dann soll der
              Knopf (bzw. die Auskunft, dass es NICHT geklappt hat) sofort stehen, nicht erst
              nach einem Neuladen. Am Entwurf UND an einem nie gesendeten Angebot bleibt
              `ablage` auf `undefined` (siehe `ablageLesen`), der Knopf fehlt also dort
              ganz — ein Klick würde der Server mit 422 ABLAGE_UNZULAESSIG abweisen, und
              das wäre keine Bedienführung, sondern eine Falle (dieselbe Regel wie am
              „Bearbeiten" einer alten Version). Die zusätzliche Prüfung auf
              `angebot.sentOn` (Schlussrunde I1) fängt den Wimpernschlag ab, in dem beim
              Wechsel des aufgeschlagenen Angebots der ALTE `ablage`-Stand noch steht,
              während `angebot` schon das NEUE (nie gesendete) zeigt. */}
          {ablage !== undefined && angebot.sentOn !== null && (
            <button type="button" className="kang__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void inAkteAblegen(); }}>
              {/* Wie an der Rechnung: „ablegen" ist das erste Mal, „erneuern" sagt, dass
                  schon etwas liegt und ersetzt wird (IDEMPOTENT, siehe `inAkteAblegen`). */}
              {ablage === null ? "PDF in Akte ablegen" : "PDF in der Akte erneuern"}
            </button>
          )}
          {angebot.darfNeueVersion && (
            <button type="button" className="kang__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void neueVersion(null); }}>
              Neue Version
            </button>
          )}
          {/* Kopieren geht IMMER — aus jedem Status, auch aus einer veralteten Version
              und auch nach abgeleitetem Auftrag (Spec). */}
          <button type="button" className="kang__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => { void kopieren(); }}>
            Kopieren
          </button>
          {angebot.status === "ACCEPTED" && (
            <button type="button" className="kang__auftrag"
              disabled={gesperrt}
              onClick={() => p.onAuftragAusAngebot(angebot)}>
              Auftrag anlegen
            </button>
          )}
        </div>
      )}

      {/* Der Ablagestand — unter den Aktionen, in gedämpfter Farbe (Randnotiz, kein
          Alarm). NUR am Angebot, das der REIHE NACH (also über die Eigenschaft `angebot`,
          nicht nur den lokalen Stand) den Entwurf verlassen hat UND wirklich gesendet
          wurde (`sentOn`, Schlussrunde I1 — sonst erschiene an einem direkt aus dem
          Entwurf zurückgezogenen Angebot fälschlich „Noch nicht in der Akte abgelegt").
          Direkt nach „Als gesendet markieren" führt der Rahmen noch die alte (DRAFT-)
          Fassung, bis er neu lädt — die FRISCHE Auskunft steht in diesem Moment in der
          Meldung oben (`pdfMeldung`, siehe `statusSetzen`), nicht hier. Zwei Stellen, die
          im selben Wimpernschlag dasselbe sagten, wären eine Text-Dopplung, an der man
          beim nächsten Fix nur eine ändert. */}
      {!bearbeitung && angebot.status !== "DRAFT" && angebot.sentOn !== null
        && ablage !== undefined && (
        ablage === null
          ? (
            <p className="kang__ablage" role="status">
              Noch nicht in der Akte abgelegt.
              {" Mit „PDF in Akte ablegen“ lässt sich das nachholen."}
            </p>
          )
          : (
            <p className="kang__ablage" role="status">
              {`Abgelegt in der Akte am ${formatDatum(ablage.filedAt)} als „${ablage.fileName}" `}
              {`— zu finden im Reiter „Dateien" dieses Kunden.`}
            </p>
          )
      )}

      {vorschauOffen && (
        <Belegvorschau
          // Über denselben Helfer wie die übrigen Versionsanzeigen (M3, Schlussrunde):
          // Massgeblich ist `versionNumber`, nicht `versionCount` — die ERSTE Version
          // einer mehrversionigen Reihe trägt kein „v1" (versionsZusatz ist leer bei 1).
          titel={`Angebot ${angebot.offerNumber}`
            + `${versionsZusatz(angebot.versionNumber) !== ""
              ? ` ${versionsZusatz(angebot.versionNumber)}` : ""}`
            + " · Vorschau"}
          // Die FASSUNG: Die Version wechselt bei jeder Änderung, und genau dann ist das
          // gezeigte Blatt nicht mehr das aktuelle (Ruling K6).
          fassung={String(angebot.version)}
          laden={() => client.vorschauOffer(kundeId, angebot.id)}
          // Der PDF-Knopf der Vorschau geht den BESTEHENDEN Download-Weg — kein zweiter
          // Weg zum selben Ziel (Spec, wörtlich: „der bestehende Download-Weg").
          aufPdf={() => { void alsPdf(); }}
          onSchliessen={() => setVorschauOffen(false)} />
      )}
    </>
  );
}
